/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import $ from 'jquery';
import Variants from '@shopify/theme-variants';
import Overlay from '@/scripts/components/Overlay';
import ScrollMagic from 'scrollmagic/scrollmagic/uncompressed/ScrollMagic';
import 'scrollmagic/scrollmagic/uncompressed/plugins/animation.gsap';
import 'scrollmagic/scrollmagic/uncompressed/plugins/debug.addIndicators';
// import TweenMax from 'gsap/src/uncompressed/TweenMax';
import {formatMoney} from '@shopify/theme-currency';
import {register} from '@shopify/theme-sections';

import {getCssVar} from '@/scripts/utils/helpers';
import {openCart} from '@/scripts/sections/minicartHelpers';
import setUpScrollIndicator from '@/scripts/components/scrollIndicator';

const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    addToCartForm: '[data-cart-submit]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',
    detailsWrapper: '.details__wrapper',
    originalSelectorId: '[data-product-select]',
    priceWrapper: '[data-price-wrapper]',
    productImageWrapper: '[data-product-image-wrapper]',
    productFeaturedImage: '[data-product-featured-image]',
    productJson: '[data-product-json]',
    productPrice: '[data-product-price]',
    productThumbs: '[data-product-single-thumbnail]',
    singleOptionSelector: '[data-single-option-selector]',
    discoverySelector: '.product__discovery__selector',
    discoveryContainer: '.product__discovery__container',

    toggleMiniCart: '[data-onclick-toggleminicart]',
};

const cssClasses = {
    activeThumbnail: 'active-thumbnail',
    hide: 'hide',
    current: 'current',
    slideIn: 'slide-in'
};

const keyboardKeys = {
    ENTER: 13,
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

register('product', {
    onLoad() {
        this.$container = $(this.container);
        this.namespace = `.${this.id}`;

        // Stop parsing if we don't have the product json script tag when loading
        // section in the Theme Editor
        if (!$(selectors.productJson, this.$container).html()) {
            return;
        }

        this.productSingleObject = JSON.parse(
            $(selectors.productJson, this.$container).html(),
        );

        const options = {
            $container: this.$container,
            enableHistoryState: this.$container.data('enable-history-state') || false,
            singleOptionSelector: selectors.singleOptionSelector,
            originalSelectorId: selectors.originalSelectorId,
            product: this.productSingleObject,
        };

        const overlay = {
            overlayEl: '.ingredients',
            trigger: '.ingredients__overlay__trigger'
        };

        this.settings = {};
        this.variants = new Variants(options);
        this.$featuredImage = $(selectors.productFeaturedImage, this.$container);

        this.$container.on(
            `variantChange${this.namespace}`,
            this.updateAddToCartState.bind(this),
        );
        this.$container.on(
            `variantPriceChange${this.namespace}`,
            this.updateProductPrices.bind(this),
        );

        if (this.$featuredImage.length > 0) {
            this.$container.on(
                `variantImageChange${this.namespace}`,
                this.updateImages.bind(this),
            );
        }

        this.initMediaQuery();
        this.initImageSwitch();
        this.ingredientOverlay(overlay);
        this.initScrollMagicScene();
        this.discoveryExplorer();
        this.slideInDetails();
        this.initToggleMiniCartButton();
        setUpScrollIndicator('.product__discovery__menu ul');

        this.setUpAddToCartGoogleTag(this.productSingleObject);
    },

    setUpAddToCartGoogleTag(product) {
        const $addToCartForm = $(selectors.addToCartForm);

        const {
            productId: id,
            title: name,
        } = product;

        const price = Number(product.price) / 100;
        const items = [{price, id, name}];

        $addToCartForm.submit(() => {
            gtag('event', 'add_to_cart', {
                'send_to': 'AW-754498206/zn7XCLX5hpgBEJ714ucC',
                'value': price,
                'currency': 'USD',
                'items': items,
            });
        });
    },

    initToggleMiniCartButton() {
        $(selectors.toggleMiniCart).click(() => {
            openCart();
        });
    },

    slideInDetails() {
        setTimeout(() => {
            let details = $(selectors.detailsWrapper);
            details.addClass(cssClasses.slideIn);
        }, 500);
    },

    initMediaQuery() {
        const small = getCssVar('small') || '750px';
        this.desktopMq = window.matchMedia(`(min-width: ${small})`);
    },

    initImageSwitch() {
        const $productThumbs = $(selectors.productThumbs, this.$container);

        if (!$productThumbs.length) {
            return;
        }

        $productThumbs
            .on('click', (evt) => {
                evt.preventDefault();
                const imageId = $(evt.currentTarget).data('thumbnail-id');
                this.switchImage(imageId);
                this.setActiveThumbnail(imageId);
            })
            .on('keyup', this.handleImageFocus.bind(this));
    },

    handleImageFocus(evt) {
        if (evt.keyCode !== keyboardKeys.ENTER) {
            return;
        }

        this.$featuredImage.filter(':visible').focus();
    },

    setActiveThumbnail(imageId) {
        let newImageId = imageId;

        // If 'imageId' is not defined in the function parameter, find it by the current product image
        if (typeof newImageId === 'undefined') {
            newImageId = $(
                `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`,
            ).data('image-id');
        }

        const $thumbnail = $(
            `${selectors.productThumbs}[data-thumbnail-id='${newImageId}']`,
        );

        $(selectors.productThumbs)
            .removeClass(cssClasses.activeThumbnail)
            .removeAttr('aria-current');

        $thumbnail.addClass(cssClasses.activeThumbnail);
        $thumbnail.attr('aria-current', true);
    },

    switchImage(imageId) {
        const $newImage = $(
            `${selectors.productImageWrapper}[data-image-id='${imageId}']`,
            this.$container,
        );
        const $otherImages = $(
            `${selectors.productImageWrapper}:not([data-image-id='${imageId}'])`,
            this.$container,
        );
        if ($newImage.length !== 0 && $otherImages.length !== 0) {
            $newImage.removeClass(cssClasses.hide);
            $otherImages.addClass(cssClasses.hide);
        }
    },

    /**
     * Updates the DOM state of the add to cart button
     *
     * @param {boolean} enabled - Decides whether cart is enabled or disabled
     * @param {string} text - Updates the text notification content of the cart
     */
    updateAddToCartState(evt) {
        const variant = evt.variant;

        console.log(`%cvariant`, 'background:#bada55; color:#222;', variant)

        if (variant) {
            $(selectors.priceWrapper, this.$container).removeClass(cssClasses.hide);
        } else {
            $(selectors.addToCart, this.$container).prop('disabled', true);
            $(selectors.addToCartText, this.$container).html(
                theme.strings.unavailable,
            );
            $(selectors.priceWrapper, this.$container).addClass(cssClasses.hide);
            return;
        }

        if (variant.available) {
            $(selectors.addToCart, this.$container).prop('disabled', false);
            $(selectors.addToCartText, this.$container).html(theme.strings.addToCart);
        } else {
            $(selectors.addToCart, this.$container).prop('disabled', true);
            $(selectors.addToCartText, this.$container).html(theme.strings.soldOut);
        }
    },

    updateImages(evt) {
        const variant = evt.variant;
        const imageId = variant.featured_image.id;

        this.switchImage(imageId);
        this.setActiveThumbnail(imageId);
    },

    /**
     * Updates the DOM with specified prices
     *
     * @param {string} productPrice - The current price of the product
     * @param {string} comparePrice - The original price of the product
     */
    updateProductPrices(evt) {
        const variant = evt.variant;
        const $comparePrice = $(selectors.comparePrice, this.$container);
        const $compareEls = $comparePrice.add(
            selectors.comparePriceText,
            this.$container,
        );

        $(selectors.productPrice, this.$container).html(
            formatMoney(variant.price, theme.moneyFormat),
        );

        if (variant.compare_at_price > variant.price) {
            $comparePrice.html(
                formatMoney(variant.compare_at_price, theme.moneyFormat),
            );
            $compareEls.removeClass(cssClasses.hide);
        } else {
            $comparePrice.html('');
            $compareEls.addClass(cssClasses.hide);
        }
    },

    /**
     * Event callback for Theme Editor `section:unload` event
     */
    onUnload() {
        this.$container.off(this.namespace);
    },

    ingredientOverlay(overlay) {
        const {overlayEl, trigger} = overlay;
        const overlayElement = document.querySelector(overlayEl);

        if (overlayElement) {
            new Overlay(overlayEl, trigger); // eslint-disable-line curly
        }
    },

    initScrollMagicScene() {
        // init controller
        const controller = new ScrollMagic.Controller({globalSceneOptions: {triggerHook: 'onEnter', duration: '200%'}});

        if (document.querySelector('.product__discovery__selector') === null) {
            const discoveryContainer = new ScrollMagic.Scene({
                triggerElement: '.discovery__container',
                triggerHook: 1,
                offset: 100,
                reverse: false
            })
                .setClassToggle('.discovery__container', 'fade-in')
                .addTo(controller);
        }

        // build scenes
        if (document.querySelector('#parallax1') != null) {
            new ScrollMagic.Scene({triggerElement: '#parallax1'})
                .setTween('#parallax1 > div', {y: '50%', ease: Linear.easeNone})
                .addTo(controller);
        }

        new ScrollMagic.Scene({triggerElement: '#parallax2'})
            .setTween('#parallax2 > div', {y: '50%', ease: Linear.easeNone})
            // .addIndicators({
            //     name: 'triggerHookTest'
            //   })
            .addTo(controller);

        new ScrollMagic.Scene({triggerElement: '#parallax3'})
            .setTween('#parallax3 > div', {y: '50%', ease: Linear.easeNone})
            .addTo(controller)
            .on('update', function (event) {
                const currentWidth = $('#parallax3').width();
                const currentHeight = $('#parallax3').height();
                // if current aspect ratio is greater, adjust bg image size
                if (currentWidth / currentHeight > 730 / 960) {
                    $('.parallaxChild').css('background-size', '100% auto');
                } else {
                    $('.parallaxChild').css('background-size', 'auto 50%');
                }

                // const triggerhook = $('div:contains("trigger triggerHookTest")')[0];
                // $(triggerhook).offset({
                //   top: window.outerHeight
                // });
                // console.log('triigerhook', triggerhook);
                // $("html, body").css({
                //   height: $(window).height()
                // });
            });

        if (document.querySelector('#parallax4') !== null) {
            new ScrollMagic.Scene({triggerElement: '#parallax4'})
                .setTween('#parallax4 > div', {y: '50%', ease: Linear.easeNone})
                .addTo(controller);
        }

        const fadeInElements = document.querySelectorAll('.fade-in');
        fadeInElements.forEach((element) => {
            const tweenElement = $(element)[0];

            const scene2 = new ScrollMagic.Scene({
                triggerElement: '#parallax4',
                duration: '70%',
                triggerHook: 'onEnter'
            })
                .setTween(tweenElement, {opacity: 1.25})
                .addTo(controller);
        });
        // conditional for the sample set
        if (document.querySelector('.product__ingredients') != null) {
            const scene3 = new ScrollMagic.Scene({
                triggerElement: '.product__ingredients',
                triggerHook: 1,
            })
                .setClassToggle('.product__ingredients', 'fade-in') // add class toggle
                // .addIndicators({
                //   name: 'about hero scroll'
                // })
                .addTo(controller);
        }
        // conditional for the sample set
        if (document.querySelector('.product__discovery__selector') === null) {
            const scene4 = new ScrollMagic.Scene({
                triggerElement: '.discovery__container',
                triggerHook: 1,
            })
                .setClassToggle('.discovery__container', 'fade-in') // add class toggle
                // .addIndicators({
                //   name: 'about hero scroll'
                // })
                .addTo(controller);
        }
    },

    discoveryExplorer() {
        if ($(selectors.discoverySelector).length !== 0) {
            $(selectors.discoverySelector).click((event) => {
                $(selectors.discoveryContainer).addClass(cssClasses.hide);
                $(selectors.discoverySelector).removeClass(cssClasses.current);
                $(event.target).addClass(cssClasses.current);
                let selector = $(event.target).attr('data-target');
                selector = 'discovery__container__' + selector;
                $(`.${selector}`).removeClass(cssClasses.hide);
            });
        }
    },
});
